import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { BookOpen, Sparkles, Eye, Map, Globe, Loader2 } from 'lucide-react';
import { basicConceptsAPI } from '@/api/basicConcepts';
import { toast } from 'sonner';
import { usePoints } from '@/context/PointsContext';
import ConceptCards from './ConceptCards';

/**
 * BasicConceptsRenderer - Main component for Basic Concepts learning experience
 * This is informative and educational - NOT a test or quiz
 */
export default function BasicConceptsRenderer({ 
  chapterId,
  onComplete 
}) {
  const [currentMode, setCurrentMode] = useState('menu'); // menu, concept-cards, stories, visual, journey, real-world
  const [gameData, setGameData] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { refreshPoints } = usePoints();
  const previousCompletionRef = useRef({ journey_completed: false, all_concepts_explored: false });

  // Load Basic Concepts data
  useEffect(() => {
    if (!chapterId) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await basicConceptsAPI.getChapterBasicConcepts(chapterId);
        // API returns { basic_concepts: {...}, user_progress: {...} }
        const basicConceptsData = data.basic_concepts || data;
        setGameData(basicConceptsData);
        const progress = data.user_progress || {
          concepts_explored_count: 0,
          concepts_explored: [],
          concept_cards_viewed: false,
          stories_viewed: false,
          visual_explorer_viewed: false,
          journey_completed: false,
          real_world_viewed: false,
          bookmarked_concepts: [],
          personal_notes: {}
        };
        setUserProgress(progress);
        
        // Initialize previous completion state
        previousCompletionRef.current = {
          journey_completed: progress.journey_completed || false,
          all_concepts_explored: progress.all_concepts_explored_at !== null
        };
      } catch (err) {
        console.error('Error loading Basic Concepts:', err);
        if (err.response?.status === 404) {
          setError('Basic Concepts have not been generated for this chapter yet. They are automatically generated when PDFs are processed.');
          toast.info('Basic Concepts will be available after PDF processing');
        } else {
          setError('Failed to load Basic Concepts. Please try again.');
          toast.error('Failed to load Basic Concepts');
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [chapterId]);

  // Update progress when concepts are explored
  const handleProgressUpdate = async (progressData) => {
    if (!chapterId) return;

    try {
      const updated = await basicConceptsAPI.updateProgress(chapterId, progressData);
      
      // Check if completion is newly achieved
      const wasJourneyCompleted = previousCompletionRef.current.journey_completed;
      const wasAllConceptsExplored = previousCompletionRef.current.all_concepts_explored;
      
      const isJourneyCompleted = updated.journey_completed || false;
      const isAllConceptsExplored = updated.all_concepts_explored_at !== null;
      
      // Detect if completion is newly achieved
      const isNewlyCompleted = 
        (!wasJourneyCompleted && isJourneyCompleted) ||
        (!wasAllConceptsExplored && isAllConceptsExplored);
      
      // Update previous completion state
      previousCompletionRef.current = {
        journey_completed: isJourneyCompleted,
        all_concepts_explored: isAllConceptsExplored
      };
      
      setUserProgress(updated);
      
      // If completion is newly achieved, trigger celebration and refresh StatsBar
      if (isNewlyCompleted) {
        // Refresh StatsBar immediately to show updated XP
        await refreshPoints();
        
        // Trigger celebration animation
        // Backend awards 10 XP for BasicConcepts completion
        const eventDetail = {
          isAlreadyCompleted: false,
          pointsEarned: 10, // 10 XP for BasicConcepts completion
          gemsEarned: 0,
          activityType: 'BasicConcepts'
        };
        
        console.log('🎉 Basic Concepts completed! Dispatching celebration event:', eventDetail);
        window.dispatchEvent(new CustomEvent('activity-completed', { detail: eventDetail }));
        
        // Show success toast
        toast.success('🎉 Basic Concepts completed! +10 XP', {
          duration: 1000
        });
      }
    } catch (err) {
      console.error('Error updating progress:', err);
      // Don't show error toast - progress updates are non-critical
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
        <span className="ml-3 text-gray-300 mt-4">Loading Basic Concepts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4 text-center">
        <p className="text-yellow-400">Basic Concepts content is not available for this chapter yet.</p>
      </div>
    );
  }

  // Main Menu
  if (currentMode === 'menu') {
    const totalConcepts = gameData.total_concepts || (gameData.concepts?.length || 0);
    const exploredCount = userProgress?.concepts_explored_count || 0;
    const progressPercent = totalConcepts > 0 ? Math.round((exploredCount / totalConcepts) * 100) : 0;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">
            🎓 {gameData.game_title || 'Basic Concepts'}
          </h2>
          <p className="text-gray-300 text-sm">
            {gameData.chapter_name || 'Chapter'}
          </p>
        </div>

        {/* Progress */}
        <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-300">Progress</span>
            <span className="text-sm font-bold text-white">{exploredCount}/{totalConcepts} Concepts</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">Estimated time: {gameData.estimated_duration || 15} minutes</p>
        </div>

        {/* Learning Modes */}
        <div className="grid grid-cols-1 gap-3">
          <Button
            onClick={() => setCurrentMode('concept-cards')}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white justify-start h-auto py-4"
          >
            <BookOpen className="w-5 h-5 mr-3" />
            <div className="text-left">
              <div className="font-bold">Concept Cards</div>
              <div className="text-xs opacity-90">Browse all concepts with explanations & examples</div>
            </div>
          </Button>

          {gameData.stories && gameData.stories.length > 0 && (
            <Button
              onClick={() => setCurrentMode('stories')}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white justify-start h-auto py-4"
            >
              <Sparkles className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-bold">Interactive Stories</div>
                <div className="text-xs opacity-90">Learn through real-world scenarios</div>
              </div>
            </Button>
          )}

          {gameData.visual_explorations && gameData.visual_explorations.length > 0 && (
            <Button
              onClick={() => setCurrentMode('visual')}
              className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white justify-start h-auto py-4"
            >
              <Eye className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-bold">Visual Explorer</div>
                <div className="text-xs opacity-90">Explore concepts through diagrams</div>
              </div>
            </Button>
          )}

          {gameData.journey_paths && gameData.journey_paths.length > 0 && (
            <Button
              onClick={() => setCurrentMode('journey')}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white justify-start h-auto py-4"
            >
              <Map className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-bold">Concept Journey</div>
                <div className="text-xs opacity-90">Follow a guided path through concepts</div>
              </div>
            </Button>
          )}

          {gameData.real_world_examples && gameData.real_world_examples.length > 0 && (
            <Button
              onClick={() => setCurrentMode('real-world')}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white justify-start h-auto py-4"
            >
              <Globe className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-bold">Real-World Connections</div>
                <div className="text-xs opacity-90">See how concepts apply in everyday life</div>
              </div>
            </Button>
          )}
        </div>

        {/* Info Message */}
        <div className="bg-blue-500/10 border border-blue-500/50 rounded-lg p-3 text-center">
          <p className="text-blue-300 text-sm">
            💡 This is a learning tool - explore concepts at your own pace. No tests or quizzes!
          </p>
        </div>
      </div>
    );
  }

  // Concept Cards Mode (Primary Mode)
  if (currentMode === 'concept-cards') {
    return (
      <ConceptCards
        concepts={gameData.concepts || []}
        userProgress={userProgress}
        onProgressUpdate={handleProgressUpdate}
        onBack={() => setCurrentMode('menu')}
      />
    );
  }

  // Other modes - placeholder for now
  return (
    <div className="space-y-4">
      <Button
        onClick={() => setCurrentMode('menu')}
        variant="outline"
        className="mb-4 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-white hover:bg-[hsl(var(--sidebar-hover))] hover:text-white hover:border-indigo-500"
      >
        ← Back to Menu
      </Button>
      <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4 text-center">
        <p className="text-yellow-400">
          {currentMode === 'stories' && 'Interactive Stories mode coming soon!'}
          {currentMode === 'visual' && 'Visual Explorer mode coming soon!'}
          {currentMode === 'journey' && 'Concept Journey mode coming soon!'}
          {currentMode === 'real-world' && 'Real-World Connections mode coming soon!'}
        </p>
      </div>
    </div>
  );
}
